import React, { useEffect } from 'react';
import moment from 'moment';
import Divider from '@mui/material/Divider';
import { useSelector, useDispatch } from 'react-redux';
import { Map } from 'immutable';
import { useTranslation } from 'src/i18n';
import { getClientId } from 'src/module/authentication/selector';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import { fetchDashboardStats, fetchSensorHistory } from 'src/module/device/action';
import { fetchAlarmHistoricalSummary } from 'src/module/alarm/action';
import { fetchNewWorkTickets } from 'src/module/workTicket/action';
import DashboardStatistics from 'src/component/UI/Statistics/DashboardStatistics';
import isEqual from 'lodash/isEqual';
import HistoricalDateRangePanel, { DateRange, getGranularityForDateRange } from 'src/component/HistoricalDateRangePanel/HistoricalDateRangePanel';

export default function DashboardHistoricalPanel () {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const clientId = useSelector(getClientId);
  const selectedFilters = useSelector(getDevicesSelectedFiltersObj, isEqual);

  const kwhLayout = [
    { 'type': 'grid', 'container': true, 'spacing': 3, 'children': [
      { 'type': 'grid', 'container': false, 'md': 12, 'xs': 12, 'children': [
        { 'type': 'block', 'title': t('deviceStats.statistics'), 'children': [
          { 'type': 'grid', 'container': true, 'spacing': 3, 'children': [
            { 'type': 'grid', 'container': false, 'md': 6, 'xs': 12, 'children': [
              { 'id': 'stat-total-kwh', 'type': 'major', 'title': t('dashboard.totalKwh'), 'value': 'dashboardStats.kwh', 'helpText': t('dashboard.totalKwhHelp'), 'defaultValue': 0, 'dynamicTitle': 'dynamickWhTitle', 'formatFunction': 'formatkWh', 'dynamicHelpText': 'dynamickWhHelpText' }
            ] },
            { 'type': 'grid', 'container': false, 'md': 6, 'xs': 12, 'children': [
              { 'id': 'stat-total-wasted', 'type': 'major', 'title': t('dashboard.totalWasted'), 'value': 'dashboardStats.forcedOnKwh', 'helpText': t('dashboard.totalWastedHelp'), 'defaultValue': '-', 'dynamicTitle': 'dynamickWhForcedOnTitle', 'formatFunction': 'formatkWh', 'dynamicHelpText': 'dynamicForcedOnkWhHelpText' }
            ] }
          ] }
        ] }
      ] }
    ] }
  ];

  const graphLayout = [
    { 'type': 'grid', 'container': true, 'spacing': 3, 'children': [
      { 'type': 'grid', 'container': false, 'md': 12, 'xs': 12, 'children': [
        { 'id': 'block-sensor-history', 'type': 'block', 'title': t('dashboard.sensorHistory'), 'children': [
          { 'type': 'component', 'componentName': 'SensorHistory' }
        ] }
      ] }
    ] }
  ];

  const dispatchDateChange = ({ startDate, endDate }: DateRange) => {
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const rollUp = getGranularityForDateRange(startDate, endDate);
    dispatch(fetchSensorHistory(selectedFilters, 'custom', rollUp, start, end));
    dispatch(fetchDashboardStats(Map(selectedFilters), start, end));
    dispatch(fetchAlarmHistoricalSummary(Map(selectedFilters)));
    dispatch(fetchNewWorkTickets(null, Map(selectedFilters), true));
  };

  useEffect(() => {
    dispatchDateChange({
      startDate: moment().subtract(3, 'months'),
      endDate: moment(),
    });
  }, [clientId, selectedFilters]);

  return (
    <HistoricalDateRangePanel
      defaultPreset='3M'
      onDateChange={dispatchDateChange}
      stats={() => <DashboardStatistics defaultLayoutOverride={kwhLayout} layoutPreferencePath='' disablePaper />}
      graph={() => (
        <>
          <Divider />
          <DashboardStatistics defaultLayoutOverride={graphLayout} layoutPreferencePath='' disablePaper />
        </>
      )}
    />
  );
}
